import { getWindowInBrowser } from 'client/RuntimeWindow';
import { context } from '@shared-services/context-service';
import { logBookingError } from './loggingUtils';
import { BookerContextType, BookingStatus, DesignStyle } from './types';

const CAL_PROVIDER_LANGUAGES_MAP: Record<string, string> = {
    fr: 'fr',
    en: 'en',
    pt: 'pt-BR',
    de: 'de',
    es: 'es',
    nl: 'nl',
};

const DEFAULT_LANGUAGE = 'en';

export const resolveCalProviderLanguage = () => {
    // if we are in server return default language.
    // locale code is not available on server for now, but could be postponed since calcom atoms renders on client only
    if (context.isServer) {
        return DEFAULT_LANGUAGE;
    }

    const { dmAPI } = getWindowInBrowser();

    const currentLocale = dmAPI?.getSiteCurrentLocale?.() || '';

    // get short language code
    const [shortLanguage] = currentLocale.split('-');

    return CAL_PROVIDER_LANGUAGES_MAP[shortLanguage] || DEFAULT_LANGUAGE;
};

export const customClassNames = {
    atomsWrapper: 'atoms-wrapper',
    bookerContainer: 'booker-container',
    eventMetaCustomClassNames: {
        eventMetaContainer: 'event-meta-container',
        eventMetaTitle: 'event-meta-title',
        eventMetaTimezoneSelect: 'event-meta-timezone-select',
    },
    datePickerCustomClassNames: {
        datePickerContainer: 'date-picker-container',
        datePickerTitle: 'date-picker-title',
        datePickerDays: 'date-picker-days',
        datePickerDate: 'date-picker-date',
        datePickerDatesActive: 'date-picker-dates-active',
        datePickerToggle: 'date-picker-toggle',
    },
    availableTimeSlotsCustomClassNames: {
        availableTimeSlotsContainer: 'available-time-slots-container',
        availableTimeSlotsHeaderContainer:
            'available-time-slots-header-container',
        availableTimeSlotsTitle: 'available-time-slots-title',
        availableTimeSlotsTimeFormatToggle:
            'available-time-slots-time-format-toggle',
        availableTimes: 'available-times',
    },
    confirmStep: {
        confirmButton: 'confirm-button',
        backButton: 'back-button ',
    },
};

export const getBookerClassNames = (designStyle: DesignStyle) => ({
    ...customClassNames,
    confirmStep: {
        ...customClassNames.confirmStep,
        backButton: `${customClassNames.confirmStep.backButton} ${
            designStyle === DesignStyle.SITE_THEME ? 'button_2' : ''
        }`,
    },
});

export const isBookingValid = (status: BookingStatus) => {
    if (!status) return false;
    return ![BookingStatus.CANCELLED, BookingStatus.REJECTED].includes(status);
};

export const isBookingAccepted = (status?: BookingStatus) => {
    if (!status) return false;
    return status === BookingStatus.ACCEPTED;
};

export const isBookingPending = (status?: BookingStatus) => {
    if (!status) return false;
    return status === BookingStatus.PENDING;
};

const validators = {
    isNonEmptyString: (value: any): value is string => {
        return !!value && typeof value === 'string';
    },
    isInteger: (value: any): value is number => {
        return Number.isInteger(value);
    },
};

type BookerParamKey = keyof Pick<
    BookerContextType,
    'clientId' | 'apiUrl' | 'eventSlug' | 'teamId' | 'language'
>;

const bookerParamRules: Record<BookerParamKey, (value: any) => boolean> = {
    clientId: validators.isNonEmptyString,
    apiUrl: validators.isNonEmptyString,
    eventSlug: validators.isNonEmptyString,
    teamId: validators.isInteger,
    language: validators.isNonEmptyString,
};

export const validateBookerReceivedParams = (
    bookerContext: BookerContextType,
    isActive: boolean
) => {
    // Only validate when booking is active
    if (!isActive) {
        return;
    }

    const invalidParams: string[] = [];

    (
        Object.entries(bookerParamRules) as [
            BookerParamKey,
            (value: unknown) => boolean
        ][]
    ).forEach(([param, validator]) => {
        if (!validator(bookerContext[param])) {
            invalidParams.push(param);
        }
    });

    if (invalidParams.length > 0) {
        logBookingError('Failed to validate booker received params', {
            additionalInfoToLog: bookerContext,
            invalidParams,
        });
    }
};
